import { Component } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { Link } from 'react-router';
import { push } from 'react-router-redux';

import styles from './Unfiled.scss';
import { documentDownloadRequest, deleteUnfiledDocument } from 'part11/actions/documents';
import { unloadDocuments, removeDocumentFromList } from 'pages/files/actions/files';
import { loadMimeTypes } from 'common/actions/fileUploadActions';
import {
    binderSelector,
    unfiledDocumentsSelector,
    loadingDocumentsSelector,
    loadingDocumentsErrorSelector,
    unfiledDocumentsCountSelector,
    documentTypePermissionSelector,
} from 'pages/files/selectors';
import { binderPermissionsSelector } from 'common/permissions/selectors';
import { UserCanUploadFiles } from 'common/permissions/components/actionButton';
import { setSortingOptions } from 'common/actions/fileSortingOptions';
import { setActionButton, clearActionButton, buildActionButtonProperty } from 'common/actions/actionButtonActions';

import { Button, BUTTON } from 'components/common/Button/Button';
const { SIZE, COLOR, BGCOLOR } = BUTTON;
import Modal from 'components/common/Modal/Modal';
import DateTime from 'common/components/DateTime/DateTime';
import PageParent from 'components/common/PageParent/PageParent';
import PageHeader from 'components/common/PageHeader/PageHeader';
import FilterBar from 'components/common/FilterBar/FilterBar';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import Table from 'components/common/Table/Table';
import FileIndicator from 'components/common/File/FileIndicator';
import QuickMenu from 'common/components/QuickMenu/QuickMenu';
import ListHelperText from 'components/common/Helper/ListHelper';
import { pathSubDirectorySelector } from 'binder/selectors/files';

import { DEFAULT_DATETIME_INPUT_FORMAT } from 'common/util/dateTime';
import { trackEvent } from 'components/common/GoogleAnalytics/GoogleAnalytics';

const sort = {
    label: 'Sort',
    queryStringParameter: 's',
    selection:[
        { displayName: 'Uploaded Date', gqlName: 'uploadDate' },
        { displayName: 'Document Name', gqlName: 'name' },
        { displayName: 'Uploaded By', gqlName: 'uploadUser' },
    ]
};

const sortOrder = {
    label: 'By',
    queryStringParameter: 'o',
    selection:[
        { displayName: 'Ascending', gqlName: '1' },
        { displayName: 'Descending', gqlName: '-1' },
    ]
};

class Unfiled extends Component {
    constructor(props) {
        super(props);
        this.modalConversionErrorContent = this.modalConversionErrorContent.bind(this);
        this.modalDuplicateErrorContent = this.modalDuplicateErrorContent.bind(this);
        this.modalContent = this.modalContent.bind(this);
        this.state = {
            initialSort: 'uploadDate',
            initialSortOrder: '-1',
            selectedDocument : {},
            modalAction : 'close',
        };
    }

    componentDidMount() {
        this.initializeQuery();
        this.initializePage();
    }

    componentWillUnmount() {
        this.props.unloadDocuments();
    }

    initializeQuery() {
        const { router, location } = this.props;
        const { initialSort, initialSortOrder } = this.state;
        const newLocation = lodash.cloneDeep(location);
        newLocation.query = {
            ...newLocation.query,
            s: initialSort,
            o: initialSortOrder,
            q: ''
        };
        router.replace(newLocation);
    }

    isBinderAvailable(binder) {
        return !lodash.isNil(binder) && !lodash.isNil(binder.id);
    }

    initializePage() {
        const { binder } = this.props;
        const { initialSort, initialSortOrder } = this.state;
        const pathName = pathSubDirectorySelector(this.props.location.pathname);
        const redirectLink = pathName === 'study' && this.props.study ?
            `/study/${this.props.study.id}/documents?folder=unfiled&offset=0&upload=true`
            :
            `/binder/${binder.id}/files?folder=unfiled&o=${initialSortOrder}&offset=0&s=${initialSort}&upload=true`;
        if (this.isBinderAvailable(binder)) {
            this.props.loadMimeTypes();
            this.actionButtonProperty = buildActionButtonProperty(
                'Add Documents',
                redirectLink,
                'uploadFiles'
            );
            this.props.setActionButton(this.actionButtonProperty);
        }
    }

    getDocumentStatusActions(status, duplicates, document) {
        const documentStatus = (!lodash.isEmpty(duplicates) || status === 'failed') ? 'failed' : 'success';

        const handlers = {
            success: () => this.goToInitialIndex(document),
            failed: () => this.callActionOnModal('documentIssueModal','open', document)
        };

        return handlers[documentStatus];
    }

    getTableColumns() {
        return [{
            header: 'Document Name',
            accessor: 'name',
            styles: {
                width: '50%'
            },
            component: document => <FileIndicator
                title={document.name}
                failedConversion={document.conversionStatus === 'failed'}
                hasDuplicate = {document.duplicates && document.duplicates.length > 0}
                onClick={this.getDocumentStatusActions(
                    document.conversionStatus,
                    document.duplicates,
                    document).bind(this)} />
        }, {
            header: 'Upload Date',
            accessor: 'uploadDate',
            styles: {
                width: '22%',
            },
            component: document => <DateTime
                className={document.className}
                time={true}
                format={'MMM D, YYYY – h:mm A'}
                value={document.uploadDate}
                inputFormat={DEFAULT_DATETIME_INPUT_FORMAT} />
        }, {
            header: 'Uploaded By',
            accessor: 'uploadUser.displayName',
            styles: {
                width: '22%',
                maxWidth: '12rem',
                wordBreak: 'break-word',
            },
        }, {
            header: '',
            accessor: '',
            styles: {
                width: 'auto',
                marginLeft: 'auto',
                overflow: 'visible'
            },
            component: document => this.formatQuickMenu(document)
        }];
    }

    formatQuickMenu(document){
        let options = [{
            label: 'Assign Document Type',
            handler: this.getDocumentStatusActions(
                document.conversionStatus,
                document.duplicates,
                document).bind(this)
        }, {
            label: 'Download Source',
            handler: () => this.downloadDocumentSource(document)
        } , {
            label: 'Delete',
            handler: () => {
                this.deleteUnfiledDocument(document);
                this.setState({selectedDocument : null});
            }
        }];

        return <QuickMenu headerText="Actions" theme="primary" options={options} />;
    }

    duplicateExistsInDuplicateModal(){
        const { selectedDocument, modalAction } = this.state;
        const modalOpen = modalAction !== 'close';
        const duplicateDocumentsCount = lodash.get(selectedDocument, 'duplicates.length', 0);
        const duplicateDocumentsExist = duplicateDocumentsCount > 0 && modalOpen;

        return duplicateDocumentsExist;
    }

    goToInitialIndex(document) {
        const { study } = this.props;
        this.setState({selectedDocument : document} , () => {
            let location = `/binder/${this.props.binder.id}/files/${document.id}/initialIndex${study.id ? `?studyId=${study.id}` : ''}`;
            
            if (lodash.get(study, 'id')) {
                location = `/study/${study.id}/files/${document.id}/initialIndex?from=unfileddocs`;
            }

            if(this.duplicateExistsInDuplicateModal()){
                this.trackEvent('Unfiled Documents Duplicate Modal', 'Continue Filing');
            }
            this.props.push(location);
        });
    }

    downloadDocumentSource(document) {
        this.props.documentDownloadRequest(document.id, null, 'ORIGINAL');
    }

    deleteUnfiledDocument(document) {
        this.setState({
            selectedDocument : document
        }, () => {
            this.props.deleteUnfiledDocument(document.id, true);
            if(this.duplicateExistsInDuplicateModal()){
                this.trackEvent('Unfiled Documents Duplicate Modal', 'Delete Document');
            }
            this.callActionOnModal('documentIssueModal','close');
            this.setState({modalAction : 'close'});
        });
    }

    callActionOnModal(modalReference, action, arg) {
        this.setState({
            selectedDocument : arg,
            modalAction : action,
        }, () => {
            this.refs[modalReference][action](arg);
        });
    }

    modalContent() {
        const displayModal = {
            duplicate : this.modalDuplicateErrorContent(),
            conversion : this.modalConversionErrorContent(),
            duplicateAndConversion : this.modalDuplicateAndConversionErrorContent(),
        };
        const { selectedDocument } = this.state;

        if(!lodash.isNil(selectedDocument)){
            const { duplicates, conversionStatus } = selectedDocument;
            const hasDuplicates = lodash.get(duplicates, 'length' , 0) > 0 ;
            const hasConversionError = conversionStatus !== 'success';

            if(hasConversionError && hasDuplicates){
                return displayModal.duplicateAndConversion;
            }

            if( hasConversionError){
                return displayModal.conversion;
            }

            if( hasDuplicates ) {
                return displayModal.duplicate;
            }
        }
    }

    modalConversionErrorContent(){
        return(
            <div className={styles.modal__group} data-cy="Unfiled">
                <h2 className={styles.modal__title}>Conversion Error</h2>
                <p className={styles.modal__description}>You will not be able to send this file for action, and this document will not be previewable.</p>
                <p className={styles.modal__description}>We recommend removing and re-uploading this document after trying one or more of the following:</p>
                <b>
                    <ul className={styles.modal__list}>
                        <li>checking file type (.PDF, .DOC, etc)</li>
                        <li>converting to a flattened PDF</li>
                        <li>removing password protection</li>
                        <li>ensuring file will open and is not corrupt</li>
                        <li>checking for unusual content</li>
                    </ul>
                </b>
            </div>
        );
    }

    modalDuplicateErrorContent(){
        const { binder : { id : binderId }, documentTypePermission } = this.props;
        const duplicateDocuments = lodash.get( this.state.selectedDocument, 'duplicates', [] );
        const hasViewPermission = duplicateDocuments.find(doc => {
            return this.getUserDocumentTypePermission(doc,  documentTypePermission);
        });

        return(
            <div className={styles.modal__group} data-cy="Unfiled-div">
                <h2 className={styles.modal__title}>Duplicate Document</h2>
                <p className={styles.modal__description}>This document is already filed in this binder. We recommend deleting this unfiled copy.
                    {hasViewPermission ? ' Click the filed documents below to view their contents.' : ''}
                </p>
                { duplicateDocuments.map( (doc, index) => {
                    const path = `/binder/${binderId}/files/${doc.documentId}`;
                    const userHasDocumentTypePermission = this.getUserDocumentTypePermission(doc,  documentTypePermission);

                    if(userHasDocumentTypePermission){
                        return (
                            <div className={styles.modal__duplicate__file} key={index} data-cy={'Unfiled' + index}>
                                <Link className={styles.link} to= {path} target="_blank">
                                    <i className={styles.icon} /><p className={styles.modal__duplicate__file__name}>{doc.documentName}</p><i className={styles.launchIcon}/>
                                </Link>
                            </div>
                        );
                    }
                }) }
            </div>
        );
    }

    getUserDocumentTypePermission(objDocument, documentTypePermission){
        return lodash.get(documentTypePermission, `${objDocument.documentTypeId}.permissions.viewPDF`, false);
    }

    trackEvent(category, action){
        trackEvent({
            category,
            action,
        });
    }

    modalDuplicateAndConversionErrorContent() {
        return(
            [
                this.modalDuplicateErrorContent(),
                this.modalConversionErrorContent(),
            ]
        );
    }

    getDocumentIssueModal() {
        return (
            <Modal ref="documentIssueModal" data-cy="Unfiled-modal">
                <div className={styles.modal__body} data-cy="Unfiled-content">
                    {this.modalContent()}
                </div>

                <div className={styles.modal__actions}>
                    <span>
                        <Button
                            text={'Continue Filing'}
                            className={styles.modal__button__left}
                            type={SIZE.SECONDARY}
                            color={COLOR.RED}
                            onClick={() => this.callActionOnModal('documentIssueModal','callFuncOnData', this.goToInitialIndex.bind(this)) } 
                            data-cy="Unfiled-Continue"/>
                    </span>

                    <span>
                        <Button
                            text={'Delete Document'}
                            type={SIZE.SECONDARY}
                            bgcolor={BGCOLOR.ORANGE}
                            color={COLOR.WHITE}
                            onClick={() => this.callActionOnModal('documentIssueModal','callFuncOnData', this.deleteUnfiledDocument.bind(this))} 
                            data-cy="Unfiled-Delete"/>
                    </span>
                </div>
            </Modal>
        );
    }

    getHelperText() {
        const { hasPermissionToUpload } = this.props;
        if(hasPermissionToUpload) {
            return `Drag-and-drop files anywhere onto this page.
                Supported file types include: PDF, DOC, XLS and PPT.
                Password-protected and .PDF Portfolios are not supported.`;
        }
        return null;
    }

    getListHelperComponent() {
        return UserCanUploadFiles(ListHelperText);
    }

    render(){
        const {
            unfiledDocumentsCount,
            loadingDocuments,
            unfiledDocuments,
            location,
            router,
            query
        } = this.props;
        const uploadHelperText = this.getHelperText();
        const ListHelperTextComponent = this.getListHelperComponent();

        return (
            <section className={styles.Unfiled} data-cy="Unfiled-section">
                <PageParent>
                    <PageHeader
                        headerText={'Unfiled Documents'}
                        subText={uploadHelperText}>
                    </PageHeader>
                    <FilterBar location={location}
                        router={router}
                        placeholder='Filter by document name'
                        sortOrderOptions={sortOrder}
                        sortOptions={sort}
                        debounceQueryMS={40} />
                    { unfiledDocumentsCount > 0 ?
                        [
                            <Table key={0} columns={this.getTableColumns()} data={unfiledDocuments} />,
                            <LoadingStatus
                                key={1}
                                itemName='Unfiled Documents'
                                loading={loadingDocuments}
                                filterQuery={query}
                                currentItemCount={unfiledDocuments.length}
                                totalItemCount={unfiledDocumentsCount}/>
                        ]
                        :
                        [
                            <p key={0} className={styles.helperText}>You have no unfiled documents</p>,
                            <ListHelperTextComponent
                                key={1}
                                primaryText={'You can drag and drop files directly onto this page'}
                                secondaryText={'supported formats: PDF, DOC, XLS and PPT.'} />
                        ]
                    }
                </PageParent>
                {this.getDocumentIssueModal()}
            </section>
        );
    }
}

export default connect((state, props) => {
    const { router, location, location: {query: {q: query, filter}} } = props;
    const binder = binderSelector(state) || props.binder;
    const study = lodash.get(state, 'study', {});
    const documentTypePermission = documentTypePermissionSelector(state);
    const unfiledDocuments = unfiledDocumentsSelector(state);
    const loadingDocuments = loadingDocumentsSelector(state);
    const loadingDocumentsError = loadingDocumentsErrorSelector(state);
    const unfiledDocumentsCount = unfiledDocumentsCountSelector(state);
    const binderPermissions = binderPermissionsSelector(state);
    return {
        binder,
        study,
        unfiledDocuments: loadingDocuments ? [] : unfiledDocuments,
        loadingDocuments,
        loadingDocumentsError,
        hasPermissionToUpload: binderPermissions.upload,
        documentTypePermission,
        unfiledDocumentsCount,
        router,
        location,
        query,
        filter
    };
}, {
    documentDownloadRequest,
    deleteUnfiledDocument,
    removeDocumentFromList,
    unloadDocuments,
    setSortingOptions,
    loadMimeTypes,
    setActionButton,
    clearActionButton,
    push
})(Unfiled);
